import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Popover } from 'primeng/popover';
import { LocalStorage } from 'src/app/shared/entity/enums';
import { ErrorMessage, ErrorType } from 'src/app/shared/entity/error.const';
import { FilterAppliedChanges } from 'src/app/shared/entity/utility-interfaces';
import { DataAccessService } from 'src/app/shared/services/data-access/data-access.service';
import { DataRepositoryService } from 'src/app/shared/services/data-repository/data-repository.service';

type RouteFilterOption = { label: string; value: string; disabled: boolean };

@Component({
    selector: 'app-routes-filter',
    templateUrl: './routes-filter.component.html',
    standalone: false
})
export class RoutesFilterComponent {

    @ViewChild('routeFilterPanel') routeFilterPanel: Popover;

    selectedRoutes: string[] = [];
    routeFilterOptions: RouteFilterOption[] = [];
  isLoadingRoutes = false;
    appliedFilterChanges: FilterAppliedChanges =  {
        isBlChanged: false,
        isTeamChanged: false,
        isRouteChanged: false,
        isRouteStateChanged: false,
        isCalendarDateChanged: false,
        isWorkshiftPatternChanged: false
    };

    constructor(
        private readonly dataAccessService: DataAccessService,
        private readonly dataRepositoryService: DataRepositoryService,
        private readonly messageService: MessageService,
        private readonly changeDetectorRef: ChangeDetectorRef
    ) {}

    /**
     * Loads the latest route list before opening the overlay.
     * The options are recomputed on each open because route availability can change
     * when team selection, preference selection, or business location changes.
     */
    toggle(event: Event): void {
        this.routeFilterPanel.toggle(event);
        this.isLoadingRoutes = true;
        this.routeFilterOptions = [];

        const blID = this.getSelectedBusinessLocationId();
        if (!blID) {
            this.isLoadingRoutes = false;
            this.routeFilterPanel.hide();
            return;
        }

        this.dataAccessService.getRouteListData(blID, this.dataRepositoryService.dateOffset, false).subscribe({
            next: (data) => {
                this.routeFilterOptions = this.buildRouteFilterOptions(data?.routes ?? []);
                // The loading state stays active until the saved and locked routes are synced,
                // so the user never sees a partially initialized selection state.
                this.syncSelectedRoutes();
                this.isLoadingRoutes = false;
                this.changeDetectorRef.markForCheck();
            },
            error: () => {
                this.showLoadError();
                this.routeFilterPanel.hide();
                this.isLoadingRoutes = false;
                this.changeDetectorRef.markForCheck();
            }
        });
    }

    /**
     * Marks the route filter as changed so the parent filter flow can react when Apply is clicked.
     */
    OnRouteFilterSelect() {
      this.appliedFilterChanges.isRouteChanged = true;
    }

    /**
     * Persist only routes that the user can actively control in this overlay.
     * Disabled routes stay enforced by team or preference state and should not be written
     * as user-managed route-filter selections.
     */
    applyFilter(): void {
      const enabledSelected = this.getEnabledSelectedRoutes();
      localStorage.setItem(LocalStorage.BLSelectedRoutes, JSON.stringify(enabledSelected.toString()));
      this.dataAccessService.setFilterApplied(true, this.appliedFilterChanges);
      this.routeFilterPanel.hide();
    }

    /**
     * Method to get currently selected business location id from local storage. 
     * This is needed to load the correct route list for the route filter options.
     * @returns Returns the selected business location id or null if not found or if the stored value is invalid.
     */
    private getSelectedBusinessLocationId(): string | null {
      const rawBusinessLocation = localStorage.getItem(LocalStorage.BusinessLocation);
      if (!rawBusinessLocation) {
        return null;
      }

      try {
        return String(JSON.parse(rawBusinessLocation));
      } catch {
        // Invalid cached BL state should not block the page with a toast.
        // Returning early keeps the overlay closed until the source state is corrected.
        return null;
      }
    }

    /**
     * Method to build route filter options for the dropdown based on the loaded route list and current selection state.
      * Disabled state is determined based on whether the route is covered by the selected cost centers or user preferences,
      * as those are enforced by other filters and should not be changeable by the user here.
      * This ensures that the UI accurately reflects the constraints coming from other active filters.
     * @param routes Route list loaded from the backend for the selected business location and date.
     * @returns Returns an array of RouteFilterOption with label, value and disabled state for each route.
     */
    private buildRouteFilterOptions(routes: any[]): RouteFilterOption[] {
      const selectedCostCenters = this.dataAccessService.filterSelection.costCenters ?? [];
      const preferenceRoutes = this.getPreferenceRoutes();

      return routes.map((route: any) => ({
        label: route.route_label,
        value: route.route_label,
        // Disabled routes are locked because another filter already owns them.
        disabled: selectedCostCenters.includes(route.cost_center_id) || preferenceRoutes.includes(route.route_label)
      }));
    }

    /**
     * Method to get the list of routes from the user's selected preference stored in local storage.
     * @returns Returns an array of route names that are part of the user's selected preference, 
     * or an empty array if no valid preference is found.
     */
    private getPreferenceRoutes(): string[] {
      const preferenceRaw = localStorage.getItem(LocalStorage.SelectedUserPreference);
      if (!preferenceRaw) {
        return [];
      }

      try {
        return JSON.parse(preferenceRaw)?.routes ?? [];
      } catch {
        return [];
      }
    }

    /**
     * resets the route filter
     */
    onReset(): void {
      try {
        this.selectedRoutes = this.getTeamsRoutes();
        localStorage.setItem(LocalStorage.BLSelectedRoutes, JSON.stringify([]));
        this.dataAccessService.filterSelection.routeNames = '';
        // Reset the filter changes state
        this.appliedFilterChanges = {
          isBlChanged: false,
          isTeamChanged: false,
          isRouteChanged: true,
          isRouteStateChanged: false,
          isCalendarDateChanged: false,
          isWorkshiftPatternChanged: false
        };
        this.dataAccessService.setFilterApplied(true, this.appliedFilterChanges);
        // Reset the appliedFilterChanges after applying filter
        this.appliedFilterChanges = {
          isBlChanged: false,
          isTeamChanged: false,
          isRouteChanged: false,
          isRouteStateChanged: false,
          isCalendarDateChanged: false,
          isWorkshiftPatternChanged: false
        };
      } catch {
      }
      // Close the popup with a null check
      if (this.routeFilterPanel) {
        this.routeFilterPanel.hide();
      }
    }
      
      /** 
     * Ensures that the selected routes in the route filter overlay are in sync with the saved route selections from local storage
     * and the currently disabled routes based on team and preference state. This keeps the UI consistent with the underlying filter state
     * and accurately reflects which routes are actively selected by the user versus which ones are enforced by other filters.
     */
    private syncSelectedRoutes(): void {
      const savedRoutes = this.getSavedRoutes();
      const lockedRoutes = this.getTeamsRoutes();

      // Locked routes are shown as selected so the UI reflects constraints coming from
      // cost center and preference state, even though the user cannot change them here.
      this.selectedRoutes = [...new Set([...savedRoutes, ...lockedRoutes])];
    }

    /**
     * Returns routes selected by team or preference selection.
     */
    private getTeamsRoutes(): string[] {
      return this.routeFilterOptions
        .filter(option => option.disabled)
        .map(option => option.value);
    }

    /**
     * Method to retrieve saved route selections from local storage. 
     * These are the routes that the user has previously selected and should be shown as selected in the UI.
     * @returns Returns an array of route names that are saved as selected in local storage, or an empty array if none are found.
     */
    private getSavedRoutes(): string[] {
      const routeNames = this.dataAccessService.filterSelection?.routeNames;

      return routeNames
        ? String(routeNames).split(',').map((routeName: string) => routeName.trim()).filter(Boolean)
        : [];
    }

    /**
     * Method to get the list of currently selected routes that are enabled (not disabled) in the route filter options.
     * @returns Returns an array of route names that are currently selected by the user and are not disabled by other filters. 
     * This is the list of routes that will be persisted when the user applies the filter changes.
     */
    private getEnabledSelectedRoutes(): string[] {
      return this.selectedRoutes.filter(selectedRoute =>
        this.routeFilterOptions.some(option => option.value === selectedRoute && !option.disabled)
      );
    }

    private showLoadError(): void {
      this.messageService.add({
        severity: ErrorType.ERROR,
        summary: ErrorType.ERROR,
        detail: ErrorMessage.BASEERRORMESSAGE
      });
    }
}
